import { useEffect, useRef, useState } from 'react';
import Dropdown, { TDropdownOption } from './Dropdown';
import Popup from '@template/components/Popup';
import Input, { IInputProps } from '@template/components/Input';
import { ChevronDown } from '@template/icons';
import IconSel from './IconSelect';

const Select = ({
  selected,
  options = [],
  onChange,
  fullWidth = false,
  disabled,
  loading,
  search,
  variant,
  popupProps,
  ...props
}: IInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((opt) => opt.key === selected);

  const [searchValue, setSearchValue] = useState(selectedOption?.label);
  const [isOpen, setIsOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  const handleSelect = (val: TDropdownOption['key']) => {
    onChange && onChange(val);
  };

  useEffect(() => {
    if (mounted) {
      const onKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Tab') {
          e.preventDefault();
          dropdownRef.current?.focus();

          window?.removeEventListener('keydown', onKeyDown);
        }
      };

      const onFocus = () => {
        setSearchValue('');

        window?.addEventListener('keydown', onKeyDown);
      };

      if (inputRef?.current && !disabled && search) {
        inputRef.current?.addEventListener('focus', onFocus);

        inputRef.current?.removeEventListener('blur', onKeyDown as any);
      }

      return () => {
        inputRef.current?.removeEventListener('focus', onFocus);

        window?.removeEventListener('keydown', onKeyDown);
      };
    }
  }, [inputRef?.current, disabled, search, mounted]);

  useEffect(() => {
    setIsOpen(false);
    setSearchValue(selectedOption?.label || '');
  }, [selected, options.length]);

  useEffect(() => {
    if (!mounted) {
      setMounted(true);
    }
  }, [mounted]);

  useEffect(() => {
    if (!isOpen) {
      setSearchValue(selectedOption?.label || '');
    }
  }, [isOpen]);

  return (
    <Popup
      isOpen={isOpen}
      minFullWidth={!fullWidth}
      onOpenChange={(val) => {
        if (disabled) return;
        if (!search) {
          setSearchValue(selectedOption?.label);
        } else {
          if (val) {
            setSearchValue('');
          } else {
            setSearchValue(selectedOption?.label);
          }
        }
        setIsOpen(val);
      }}
      popupContent={
        <Dropdown
          ref={dropdownRef}
          onChange={handleSelect}
          setIsOpen={setIsOpen}
          options={
            search
              ? options?.filter((opt) =>
                  opt.label.toLowerCase().includes(searchValue || '')
                )
              : options
          }
          selected={selected}
        />
      }
      placement={'bottom-start'}
      unstyled
      {...popupProps}
    >
      <Input
        placeholder={search ? 'Search...' : 'Select an option'}
        value={searchValue}
        variant={variant}
        onChange={setSearchValue}
        ref={inputRef}
        disabled={disabled}
        loading={loading}
        fullWidth={fullWidth}
        readOnly={!search}
        {...props}
        endContent={
          <ChevronDown
            style={{
              height: '100%',
              transform: `rotate(${isOpen ? '180deg' : '0'})`,
              transition: 'transform 0.2s ease',
            }}
          />
        }
      />
    </Popup>
  );
};

export const IconSelect = IconSel;

export default Select;
